import React from 'react';
import { Menu, Moon, Sun, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { routes } from '@/lib/routes';
import { useAppStore } from '@/stores/app';
import { useNavigationStore } from '@/stores/navigation';

export const Header = React.memo(function Header() {
  const navigate = useNavigate();
  const { theme, setTheme } = useAppStore();
  const { toggleNavCollapsed } = useNavigationStore();

  const getThemeIcon = React.useCallback(() => {
    return theme === 'light'
      ? <Sun className="h-4 w-4" aria-hidden="true" />
      : <Moon className="h-4 w-4" aria-hidden="true" />;
  }, [theme]);

  const getThemeLabel = React.useCallback(() => {
    return theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme';
  }, [theme]);

  const cycleTheme = React.useCallback(() => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(nextTheme);
  }, [theme, setTheme]);

  const handleGoToDashboard = React.useCallback(() => {
    navigate(routes.HOME);
  }, [navigate]);

  return (
    <header 
      className="sticky top-0 z-40 w-full h-16 border-b border-header-border bg-header-background text-header-foreground"
      role="banner"
    >
      <div className="flex h-full items-center justify-between px-4 sm:px-6 lg:px-8"> 
        {/* Left: Brand */}
        <div className="flex items-center">
          <img 
            src="/LOGO-HF-PNG-WHITE.png"   
            alt="HelloFuture logo" 
            className="h-10 w-auto"
            onError={(e) => {
              console.warn('Failed to load header logo');
              e.currentTarget.style.display = 'none';
            }}
            loading="eager"
          />
        </div>

        {/* Right: Theme/Profile on desktop, Hamburger on mobile */}
        <div className="flex items-center gap-2" role="toolbar" aria-label="User actions">
          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={cycleTheme}
              className="h-9 w-9 p-0 text-header-foreground hover:bg-header-muted/20"
              aria-label={getThemeLabel()}
              title={getThemeLabel()}
            >
              {getThemeIcon()}
            </Button>
            
            {/* Profile Avatar Placeholder */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoToDashboard}
              className="h-9 w-9 p-0 rounded-full text-header-foreground hover:bg-header-muted/20"
              aria-label="Go to dashboard"
              title="Go to dashboard"
            >
              <div className="h-7 w-7 rounded-full bg-header-muted flex items-center justify-center">
                <User className="h-4 w-4 text-header-foreground" aria-hidden="true" />
              </div>
            </Button>
          </div>

          {/* Mobile hamburger */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleNavCollapsed}
            className="h-9 w-9 p-0 lg:hidden text-header-foreground hover:bg-header-muted/20" 
            aria-label="Toggle navigation menu"
            aria-expanded="false"
          >
            <Menu className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </header>
  );
});